import React, { useState, useRef, useEffect } from "react";
import dcReviews from "@/core/Reviews/reviews-dc.json";
import type { Review } from "@/core/Reviews/types";
import {
  animateTitle,
  animateTestimonialEntrance,
  animateTestimonialTransition,
  cleanupScrollTriggers,
} from "./gsap";

const MAX_TESTIMONIALS = 10;

const testimonials: Review[] = (dcReviews as Review[]).slice(
  0,
  MAX_TESTIMONIALS
);

const TestimonialsSlider: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const quoteRef = useRef<HTMLParagraphElement>(null);
  const authorRef = useRef<HTMLDivElement>(null);

  const goToNext = () => {
    setCurrent((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  const goToPrev = () => {
    setCurrent((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  // Initial animation setup
  useEffect(() => {
    const animationTimeout = setTimeout(() => {
      // Animate the title
      animateTitle(
        { current: titleRef.current },
        { current: containerRef.current }
      );

      // Animate the initial testimonial content
      animateTestimonialEntrance(
        { current: containerRef.current },
        { current: quoteRef.current },
        { current: authorRef.current }
      );
    }, 100);

    // Clean up
    return () => {
      clearTimeout(animationTimeout);
      cleanupScrollTriggers();
    };
  }, []);

  // Animate testimonial transitions when current changes
  useEffect(() => {
    // Skip the initial render
    if (quoteRef.current && authorRef.current) {
      animateTestimonialTransition(
        { current: quoteRef.current },
        { current: authorRef.current }
      );
    }
  }, [current]);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(goToNext, 8000);
    return () => clearInterval(interval);
  }, []);

  if (testimonials.length === 0) return null;

  const testimonial = testimonials[current];

  return (
    <div ref={containerRef} className="py-16 bg-law-black text-white">
      <div className="container mx-auto px-4">
        <h2
          ref={titleRef}
          className="text-3xl md:text-4xl font-bold text-center mb-12"
        >
          What Our Customers Say
        </h2>
        <div className="max-w-3xl mx-auto relative">
          <div className="flex flex-col items-center text-center">
            <svg
              className="w-12 h-12 mb-6 text-cannabis-green-400"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
            >
              <path d="M0 216C0 149.7 53.7 96 120 96h8c17.7 0 32 14.3 32 32s-14.3 32-32 32h-8c-30.9 0-56 25.1-56 56v8h64c35.3 0 64 28.7 64 64v64c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V320 288 216zm256 0c0-66.3 53.7-120 120-120h8c17.7 0 32 14.3 32 32s-14.3 32-32 32h-8c-30.9 0-56 25.1-56 56v8h64c35.3 0 64 28.7 64 64v64c0 35.3-28.7 64-64 64H320c-35.3 0-64-28.7-64-64V320 288 216z" />
            </svg>
            <p ref={quoteRef} className="text-lg md:text-xl italic mb-8">
              "{testimonial.review}"
            </p>
            <div
              ref={authorRef}
              className="font-medium flex flex-col items-center"
            >
              {testimonial.avatar && (
                <img
                  src={testimonial.avatar}
                  alt={
                    testimonial.name
                      ? `${testimonial.name}'s avatar`
                      : "Customer avatar"
                  }
                  className="w-10 h-10 rounded-full mb-2"
                  loading="lazy"
                />
              )}
              <span className="block text-cannabis-green-300">
                {testimonial.name || "Anonymous"}
              </span>
              <span className="text-sm opacity-75">
                {testimonial.source || "Google"}
              </span>
            </div>
          </div>
          {/* Navigation dots */}
          <div className="flex justify-center space-x-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrent(index)}
                className={`w-3 h-3 rounded-full ${
                  current === index ? "bg-cannabis-green-400" : "bg-gray-600"
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              ></button>
            ))}
          </div>
          {/* Navigation arrows */}
          <button
            onClick={goToPrev}
            className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-full hidden md:block text-white hover:text-cannabis-green-300 transition-colors"
            aria-label="Previous testimonial"
          >
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <button
            onClick={goToNext}
            className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-full hidden md:block text-white hover:text-cannabis-green-300 transition-colors"
            aria-label="Next testimonial"
          >
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestimonialsSlider;
